/**
 * ImportProgress - Progress display for async CSV imports
 * 
 * Features:
 * - Real-time progress bar
 * - Row processing counter
 * - Estimated time remaining
 * - Cancel button
 */

import { useMemo } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Progress } from './ui/progress';
import {
  Loader2,
  XCircle,
  Clock,
  FileSpreadsheet,
} from 'lucide-react';

/**
 * ImportProgress component
 * 
 * @param {Object} props
 * @param {Object} props.job - Job status from backend
 * @param {number} props.totalRows - Total rows being imported (until the job reports its own)
 * @param {Function} props.onCancel - Cancel import callback
 */
export function ImportProgress({
  job,
  totalRows = 0,
  onCancel,
}) {
  const { t } = useLanguage();

  // The job reports its progress as an object ({ current_row, total_rows, imported, ... }); current_row is
  // the last processed data row. Before the first poll the job has no such object yet.
  const currentRow = Number(job?.progress?.current_row) || 0;
  const total = Number(job?.progress?.total_rows) || totalRows;

  // Calculate progress percentage
  const progressPercent = total ? Math.min(Math.round((currentRow / total) * 100), 100) : 0;

  // Estimate remaining time
  const estimatedTime = useMemo(() => {
    if (!job?.started_at || !currentRow || currentRow === 0) return null;

    const startTime = new Date(job.started_at).getTime();
    const elapsed = Date.now() - startTime;
    const rowsRemaining = total - currentRow;
    
    if (elapsed <= 0 || currentRow <= 0) return null;
    
    const msPerRow = elapsed / currentRow;
    const remainingMs = rowsRemaining * msPerRow;
    
    // Don't show estimate if less than 5 seconds
    if (remainingMs < 5000) return null;
    
    const seconds = Math.ceil(remainingMs / 1000);
    
    if (seconds < 60) {
      return `~${seconds}s`;
    }
    
    const minutes = Math.ceil(seconds / 60);
    return `~${minutes}m`;
  }, [job, currentRow, total]);

  return (
    <div className="space-y-6">
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="p-8">
          <div className="flex flex-col items-center text-center space-y-6">
            {/* Animated Icon */}
            <div className="relative">
              <div className="absolute inset-0 bg-primary/20 rounded-full animate-ping" />
              <div className="relative p-6 bg-primary/10 rounded-full">
                <Loader2 className="h-12 w-12 text-primary animate-spin" />
              </div>
            </div>

            {/* Title */}
            <div>
              <h3 className="text-xl font-semibold text-slate-800">
                {t.import?.progressTitle || 'Import in progress'}
              </h3>
              <p className="text-sm text-slate-500 mt-1">
                {t.import?.progressSubtitle || 'Please wait while data is being imported...'}
              </p>
            </div>

            {/* Progress Bar */}
            <div className="w-full max-w-md space-y-2">
              <Progress 
                value={progressPercent} 
                className="h-3"
                data-testid="import-progress-bar"
              />
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600 font-medium">
                  {progressPercent}%
                </span>
                <span className="text-slate-500 flex items-center gap-2">
                  <FileSpreadsheet className="h-4 w-4" />
                  {t.import?.processingRow?.replace('{current}', currentRow).replace('{total}', total) ||
                    `Row ${currentRow} of ${total}`}
                </span>
              </div>
            </div>

            {/* Estimated Time */}
            {estimatedTime && (
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Clock className="h-4 w-4" />
                <span>
                  {t.import?.estimatedTime || 'Estimated remaining'}: {estimatedTime}
                </span>
              </div>
            )}

            {/* Cancel Button */}
            <Button
              variant="outline"
              onClick={onCancel}
              className="gap-2 text-slate-600 hover:text-red-600 hover:border-red-300"
              data-testid="cancel-import-btn"
            >
              <XCircle className="h-4 w-4" />
              {t.import?.cancelImport || 'Cancel Import'}
            </Button>

            {/* Warning Notice */}
            <p className="text-xs text-slate-400 max-w-sm">
              {t.import?.cancelWarning || 'Note: Already imported rows will not be rolled back.'}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default ImportProgress;
